'use client';

import { Suspense } from 'react';

import { useMarketingScrolled } from '../context/marketing-scroll-context';
import { MarketingAuthActions } from './marketing-auth-actions';
import { MarketingLogo } from '@naumu/ui/components/marketing-logo';
import { MarketingMobileNav } from './marketing-mobile-nav';
import { MarketingNavCrawlLinks } from './marketing-nav-crawl-links';
import { MarketingNavLinks } from './marketing-nav-links';

/**
 * MarketingHeader - sticky public header shared across all (marketing) pages.
 * Transparent over the hero, blurs + darkens once `scrolled` (from the shell's
 * scroll context). Logo · nav links · auth actions.
 */
export const MarketingHeader = () => {
	const scrolled = useMarketingScrolled();

	return (
		<nav aria-label="Primary" className="sticky top-0 z-50 pb-4" data-scrolled={scrolled}>
			{/*
			  SSR-crawlable mirror of the hover-only popover links: the nav's
			  sub-links only mount inside MarketingNavLinks' AnimatePresence
			  popover (closed on first paint), so they are absent from the
			  server HTML. This sr-only list puts them there, sourced from the
			  same NAV_LINKS config the popovers read.
			*/}
			<MarketingNavCrawlLinks />
			{/*
			  Gradient + blur + fade-out mask live on this dedicated layer BEHIND the
			  content, never on the <nav> itself. A mask (like backdrop-filter) clips
			  ALL of an element's descendants to the masked region - so a mask on the
			  <nav> clipped the nav popover (which overflows below the header) the
			  instant the scrolled mask kicked in. As a separate sibling layer it
			  paints the header background without ever clipping the popover.
			  NOTE: the blur here is a deliberate, user-approved exception to the
			  no-backdrop-blur scrim rule (design.md) - it is a short header band,
			  not a full-screen scrim, and the blurred treatment is the chosen look.
			*/}
			<div
				aria-hidden
				className="pointer-events-none absolute inset-0 transition-all duration-300"
				style={{
					backdropFilter: scrolled ? 'blur(20px)' : 'none',
					WebkitBackdropFilter: scrolled ? 'blur(20px)' : 'none',
					background: scrolled
						? 'linear-gradient(0deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.25) 27%, rgba(0,0,0,0.45) 49%, rgba(0,0,0,0.7) 80%, rgba(0,0,0,0.9) 100%)'
						: 'linear-gradient(to bottom, rgba(0,0,0,0.6) 0%, transparent 100%)',
					maskImage: scrolled ? 'linear-gradient(black 75%, transparent 100%)' : undefined,
					WebkitMaskImage: scrolled ? 'linear-gradient(black 75%, transparent 100%)' : undefined,
				}}
			/>
			{/*
			  Two-cluster flex row (`justify-between`): logo + nav links form ONE left
			  cluster (D1 - left-aligned nav), auth actions stay pinned right. No grid
			  tracks needed here - MarketingNavLinks is display:none below md, so the
			  left cluster's width just shrinks to the logo and `justify-between` keeps
			  auth pinned right on mobile too.
			*/}
			<div className="mk-container mk-pad relative flex h-20 items-center justify-between gap-3 md:gap-6">
				{/* Left cluster: mobile burger (md:hidden) + logo, then the desktop nav
				    links right after with a comfortable gap. The burger opens the shared
				    nav drawer that carries the full nav on small screens. */}
				<div className="flex items-center gap-1 md:gap-6">
					<div className="flex items-center gap-1">
						<MarketingMobileNav />
						<MarketingLogo href="/" className="shrink-0" />
					</div>

					<MarketingNavLinks />
				</div>

				<div className="flex items-center gap-3">
					<Suspense
						fallback={<div className="h-9 w-24 animate-pulse rounded-[12px] bg-white/5" />}
					>
						<MarketingAuthActions />
					</Suspense>
				</div>
			</div>
		</nav>
	);
};
